import React, {
  createContext,
  PropsWithChildren,
  useMemo,
  useEffect,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import * as signalR from '@microsoft/signalr';
import {
  HubConnection,
  IHttpConnectionOptions,
  LogLevel,
} from '@microsoft/signalr';
import {
  getAccessTokenFromLocalStorage,
} from 'utils/auth';
import {
  baseApiUrl,
} from 'env';
import signalRConfig from 'config/signalRConfig';
import {
  loadImpersonationHeader,
  maybeAttachSavedImpersonationHeader,
} from 'utils/impersonation';

type SignalRHubContextProps = {
  hubConnection: HubConnection | null;
};

export const SignalRHubContext = createContext<SignalRHubContextProps>({
  hubConnection: null,
});

const getHubOptions = (): IHttpConnectionOptions => {
  const cachedTokenItem = getAccessTokenFromLocalStorage();

  const options: IHttpConnectionOptions = {
    withCredentials: false, // true when we have allowed credentials in the server
    skipNegotiation: false, // keep it false always
    transport: signalR.HttpTransportType.WebSockets,
    accessTokenFactory(): string {
      return cachedTokenItem?.token ?? '';
    },
  };

  options.headers = {
    ...(options.headers ?? {}),
    Authorization: `Bearer ${cachedTokenItem?.token}`,
  };
  maybeAttachSavedImpersonationHeader(options.headers);

  return options;
};

export function getNewHubConnection(): HubConnection {
  const baseEndpoint = `${baseApiUrl}/${signalRConfig.signalRPrefix}/notifications`;

  // Add impersonation header to query string for WebSocket connections
  // WebSocket connections in browsers cannot send custom headers,
  // so we need to pass it as a query parameter
  const impersonationHeader = loadImpersonationHeader();
  const endpoint = !isNil(impersonationHeader)
    ? (() => {
      const url = new URL(baseEndpoint);
      url.searchParams.set('x-impersonation', impersonationHeader);
      return url.toString();
    })()
    : baseEndpoint;

  const options: IHttpConnectionOptions = getHubOptions();
  const loggerType = signalRConfig.isLogEnabled
    ? LogLevel.Information
    : LogLevel.Error;

  return new signalR
    .HubConnectionBuilder()
    .withUrl(endpoint, options)
    .withAutomaticReconnect(signalRConfig.retry)
    .withServerTimeout(60000)
    .configureLogging(loggerType)
    .build();
}

type SignalRHubProviderProps = PropsWithChildren<{
  canUseRealTime?: boolean;
}>;

export default function SignalRHubProvider({
  children,
  canUseRealTime = true,
}: SignalRHubProviderProps) {
  const [hubConnection, setHubConnection] = useState<HubConnection | null>(null);

  useEffect(() => {
    if (!canUseRealTime) {
      setHubConnection(null);
      return undefined;
    }

    let isDisposed = false;
    const connection = getNewHubConnection();

    const createConnection = async () => {
      try {
        await connection.start();

        if (isDisposed) {
          await connection.stop();
          return;
        }

        setHubConnection(connection);
      } catch (error) {
        if (!isDisposed) {
          console.error('Error creating connection', error);
        }
      }
    };

    createConnection();

    return () => {
      isDisposed = true;
      setHubConnection((currentConnection) => (
        currentConnection === connection
          ? null
          : currentConnection
      ));
      connection.stop().catch((error) => {
        console.error('Error stopping connection', error);
      });
    };
  }, [canUseRealTime]);

  const value = useMemo(() => ({
    hubConnection,
  }), [hubConnection]);

  return (
    <SignalRHubContext.Provider value={value}>
      {children}
    </SignalRHubContext.Provider>
  );
}
